/**
 * ServicePropertyVisualitySettings описывает настройки отображения
 * значений свойства
 */
export class ServicePropertyVisualitySettings {
  /**
   * Флаг позволяет скрыть отображение заголовка свойства.
   * В качестве заголовка используется название свойства.
   */
  public IsNeedHideCaption: boolean = false

  /**
   * Положение заголовка для свойства
   * - top - сверху от значения
   * - left - слева от значения
   */
  public CaptionPosition: "top" | "left" = "left"

  /**
   * Если свойство является списочным, то включает отображение
   * описания опций, если они есть.
   */
  public IsNeedShowDescriptionForOption: boolean = true

  /**
   * Свойство позволяет задать положение описания.
   *  - inner - в одну строку со значением, через запятую
   *  - bottom - строкой ниже, более мелким шрифтом
   */
  public OptionDescriptionPosition: "inner" | "bottom" = "inner"

  /**
   * Приоритет вывода свойства в списке. Чем ниже значение, тем выше
   * будет выведено свойство.
   */
  public PropertyPriority: number = 0

  /**
   * Вариант отображения значения свойства.
   * - normal - обычное отображение, стандартным шрифтом
   * - small - уменьшенный текст, оформление как у заголовка
   */
  public ValueDisplayVariant: "small" | "normal" = "normal"

  /**
   * Флаг позволяет выводить свойства изначально скрытыми.
   * Свойства будут выводиться в блоке описания и показываться при
   * разворачивании свойств.
   */
  public IsShowValueInDescription: boolean = false

  /**
   * Префикс и суффикс для значения свойства.
   * Может содержать локализацию.
   */
  public PropertyValuePrefix: string = ""
  public PropertyValueSuffix: string = ""

  /**
   * Значение для включенного и выключенного значения переключателя.
   * Может быть локализовано.
   */
  public ValueForSelectedSwitch: string = ""
  public ValueForUnselectedSwitch: string = ""

  /**
   * Заглушка для запрета прямого создания объектов данного типа
   * @private
   */
  private constructor() {
  }

  /**
   * RestoreSettings выполняет восстановление данных объекта настроек
   * Функция выполняет нормализацию значений настроек.
   * Функция дополняет недостающие настройки значениями по умолчанию.
   *
   * @param data
   */
  public static RestoreSettings(data: any): ServicePropertyVisualitySettings {
    let result = new ServicePropertyVisualitySettings();

    if (typeof data === "string") {
      data = JSON.parse(data)
    }

    if (typeof data !== "object") {
      return result;
    }

    Object.keys(result).map((key: keyof ServicePropertyVisualitySettings) => {
      // @ts-ignore
      result[key] = ServicePropertyVisualitySettings.GetCorrectFieldValue(key, data[key])
    })

    return result;
  }

  /**
   * GetCorrectFieldValue выполняет восстановление данных поля из переданного значения настроек.
   * Данный метод так же проводит валидацию значения каждой из настроек поля. Метод возвращает
   * значение по умолчанию, если значение передано не корректно.
   */
  public static GetCorrectFieldValue<T extends keyof ServicePropertyVisualitySettings>(field: T, data: any): any {
    let defaults = new ServicePropertyVisualitySettings()

    if (data === undefined) {
      return defaults[field]
    }

    switch (field) {
      case "PropertyPriority":
        return typeof data === "number" && data >= 0 ? data : defaults[field]
      case "ValueForSelectedSwitch":
      case "ValueForUnselectedSwitch":
      case "PropertyValuePrefix":
      case "PropertyValueSuffix":
        return typeof data === "string" ? data : defaults[field]
      case "IsNeedHideCaption":
      case "IsNeedShowDescriptionForOption":
      case "IsShowValueInDescription":
        return !!data
      case "CaptionPosition":
        return typeof data === "string" && ["top", "left"].includes(data) ? data : defaults[field]
      case "OptionDescriptionPosition":
        return typeof data === "string" && ["inner", "bottom"].includes(data) ? data : defaults[field]
      case "ValueDisplayVariant":
        return typeof data === "string" && ["small", "normal"].includes(data) ? data : defaults[field]
    }

    return defaults[field];
  }
}